
import { supabase } from './supabaseService';
import { TriggerType, AutomationFlow, AutomationStep } from '../types';

import { dispatchGlobalWebhook, notifyN8nEvent, postWebhookProxyOrDirect } from './webhookService';

export type AutomationContext = {
    vacancyId?: string;
    candidateId: string;
    stageId?: string;
    stageName?: string;
    previousStatus?: string;
    newStatus?: string;
    clientId?: string;
    profileId?: string;
    candidateName?: string;
    source?: string;
    user_id?: string;
    customEventName?: string;
};

export const triggerCandidateStatusChanged = async (ctx: {
    candidateId: string;
    profileId?: string;
    clientId?: string;
    previousStatus?: string;
    newStatus: string;
    source?: 'talentpool_list' | 'talentpool_detail' | 'talentpool_grid';
    user_id?: string;
    candidateName?: string;
}) => {
    if (ctx.previousStatus != null && ctx.previousStatus === ctx.newStatus) return;
    const automationContext: AutomationContext = {
        candidateId: ctx.candidateId,
        profileId: ctx.profileId ?? ctx.candidateId,
        clientId: ctx.clientId,
        previousStatus: ctx.previousStatus,
        newStatus: ctx.newStatus,
        source: ctx.source,
        user_id: ctx.user_id,
        candidateName: ctx.candidateName,
    };
    await notifyN8nEvent('candidate_status_changed', automationContext);
    await triggerAutomation('candidate_status_changed', automationContext, { skipN8nNotify: true });
};

export const triggerAutomation = async (
    trigger: TriggerType,
    context: AutomationContext,
    options?: { skipN8nNotify?: boolean }
) => {
    console.log(`[Automation] Triggering ${trigger} for candidate ${context.candidateId}`);

    if (!options?.skipN8nNotify) {
        dispatchGlobalWebhook(trigger, context);
    }

    try {
        // 1. Fetch active flows matching the trigger
        // Activeer flows die matchen op de specifieke trigger OF op 'all'
        const { data: flows, error } = await supabase
            .from('automation_flows')
            .select('id, name, trigger_type, status, client_id, tags, steps:automation_steps(id, type, label, config, step_order)')
            .or(`trigger_type.eq.${trigger},trigger_type.eq.all`)
            .eq('status', 'active');

        if (error || !flows || flows.length === 0) {
            console.log("[Automation] No active flows found.");
            return;
        }

        // 2. Filter by custom event or target phase (if applicable)
        const matchedFlows = flows.filter(flow => {
            if (trigger === 'custom_event') {
                const requiredEventName = flow.trigger_config?.customEventName;
                if (!requiredEventName) return false;
                return requiredEventName.toLowerCase() === context.customEventName?.toLowerCase();
            }
            if (trigger === 'stage_changed') {
                const targetStage = flow.trigger_config?.targetStageName;
                if (!targetStage) return true; // Empty means "any phase change"
                return targetStage.toLowerCase() === context.stageName?.toLowerCase();
            }
            if (trigger === 'candidate_status_changed') {
                const targetStatus = flow.trigger_config?.targetStageName;
                if (!targetStatus) return true;
                return targetStatus.toLowerCase() === context.newStatus?.toLowerCase();
            }
            return true;
        });

        if (matchedFlows.length === 0) {
            console.log("[Automation] No matching flows after filtering.");
            return;
        }

        for (const flow of matchedFlows) {
            console.log(`[Automation] Executing flow: ${flow.name}`);
            await executeFlow(flow, context);
        }

    } catch (e) {
        console.error("[Automation] Error triggering automation:", e);
    }
};

const executeStepsSequence = async (steps: any[], context: any, flow: any, executionTrace: any[] = []) => {
    // Sort steps
    const sortedSteps = [...steps].sort((a: any, b: any) =>
        (a.order_index ?? a.step_order ?? 0) - (b.order_index ?? b.step_order ?? 0)
    );
    let isSuccess = true;
    let stepsCompleted = 0;

    for (const step of sortedSteps) {
        try {
            await executeStep(step, context, flow, executionTrace);
            stepsCompleted++;
        } catch (e) {
            console.error(`[Automation] Step ${step.id} failed:`, e);
            executionTrace.push({ stepId: step.id, type: step.type || step.step_type, label: step.label, status: 'failed', details: (e as any).message || String(e), timestamp: Date.now() });
            isSuccess = false;
            break; // Stop execution on failure
        }
    }
    return { isSuccess, stepsCompleted };
};

const executeFlow = async (flow: any, context: any) => {
    const executionTrace: any[] = [];
    const { isSuccess, stepsCompleted } = await executeStepsSequence(flow.steps || [], context, flow, executionTrace);

    // Write Execution Log
    try {
        const traceSnapshot = JSON.parse(JSON.stringify(executionTrace)); // Deep clone to prevent reference loss
        console.log(`[Automation] Final trace payload before insert:`, traceSnapshot);

        const { error: logErr } = await supabase.from('automation_logs').insert({
            flow_id: flow.id,
            client_id: flow.client_id || context.userData?.client_id || null, // Assuming flows might be client-specific
            trigger_event: flow.trigger_type || flow.trigger || 'unknown',
            status: isSuccess ? 'success' : 'failed',
            step_count: stepsCompleted,
            execution_trace: traceSnapshot
        });
        if (logErr) throw logErr;
        console.log(`[Automation] Log saved for flow: ${flow.name} (${isSuccess ? 'success' : 'failed'})`);
    } catch (logErr) {
        console.error("[Automation] Failed to write automation log to Supabase:", logErr);
    }
};

// Condition Evaluators
const resolveFieldValue = (fieldPath: string, context: any): any => {
    if (!fieldPath) return undefined;
    const c = context.candidateData || {};
    const v = context.vacancyData || {};
    const [category, fieldName] = fieldPath.split('.');

    if (category === 'Candidate') {
        if (fieldName === 'stage') return context.stageName;
        if (fieldName === 'score') return c.score;
        if (fieldName === 'source') return c.source;
    } else if (category === 'Job') {
        if (fieldName === 'title') return v.title;
        if (fieldName === 'type') return v.type;
        if (fieldName === 'department') return v.department;
        if (fieldName === 'location') return v.location || v.city;
    } else {
        // Fallbacks for older formats
        if (fieldPath === 'candidate.stage') return context.stageName;
        if (fieldPath === 'candidate.score') return c.score;
        if (fieldPath === 'vacancy.type') return v.type;
        if (fieldPath === 'client.company') return v.company;
        if (fieldPath === 'recruiter.name') return context.userData?.name;
    }
    return undefined;
};

const checkCondition = (condition: any, context: any): boolean => {
    const actualValue = resolveFieldValue(condition.field, context);
    const expectedValue = condition.value;

    switch (condition.operator) {
        case 'equals': return String(actualValue).toLowerCase() === String(expectedValue).toLowerCase();
        case 'contains': return String(actualValue).toLowerCase().includes(String(expectedValue).toLowerCase());
        case 'greater_than': return Number(actualValue) > Number(expectedValue);
        case 'is_set': return actualValue !== undefined && actualValue !== null && actualValue !== '';
        default: return false;
    }
};

const evaluateBranch = (branch: any, context: any): boolean => {
    if (!branch.conditionGroups || branch.conditionGroups.length === 0) return true; // Default branch passes

    for (const group of branch.conditionGroups) {
        if (!group.conditions || group.conditions.length === 0) continue;
        const type = group.type || 'and';
        let groupPasses = type === 'and' ? true : false;

        for (const cond of group.conditions) {
            const passes = checkCondition(cond, context);
            if (type === 'and' && !passes) { groupPasses = false; break; }
            if (type === 'or' && passes) { groupPasses = true; break; }
        }
        if (!groupPasses) return false;
    }
    return true;
};

const parseVariables = (text: string, context: any) => {
    if (!text) return '';
    let parsed = text;
    const c = context.candidateData || {};
    const v = context.vacancyData || {};
    const u = context.userData || {};

    // Derived simple fields
    // Often c is a candidate_client_profile which nests the true candidate data inside c.candidate
    const actualCandidate = c.candidate || c;
    const candidateName = actualCandidate.name || 'Kandidaat';
    const firstName = candidateName.split(' ')[0];
    const candidateEmail = actualCandidate.email || '';
    const candidatePhone = actualCandidate.phone || '';
    const jobTitle = v.title || 'de vacature';
    const recruiterName = u.name || 'Je contactpersoon';

    parsed = parsed.replace(/\{\{candidate\.first_name\}\}/gi, firstName);
    parsed = parsed.replace(/\{\{candidate\.name\}\}/gi, candidateName);
    parsed = parsed.replace(/\{\{candidate\.email\}\}/gi, candidateEmail);
    parsed = parsed.replace(/\{\{candidate\.phone\}\}/gi, candidatePhone);
    parsed = parsed.replace(/\{\{job\.title\}\}/gi, jobTitle);
    parsed = parsed.replace(/\{\{company\.name\}\}/gi, 'Werftools'); // Can map from client later
    parsed = parsed.replace(/\{\{recruiter\.name\}\}/gi, recruiterName);

    return parsed;
};

const executeStep = async (step: any, context: any, flow: any, executionTrace: any[] = []) => {
    const stepType = step.type || step.step_type;
    console.log(`[Automation] Executing step: ${stepType} - ${step.label}`);

    if (!context.vacancyData && context.vacancyId) {
        const { data: vData } = await supabase.from('vacancies').select('*').eq('id', context.vacancyId).single();
        if (vData) context.vacancyData = vData;
    }

    // Fetch full context if necessary and resolve profile ID
    if (!context.candidateData && context.candidateId) {
        let { data: cData } = await supabase.from('candidates').select('*, candidate:candidates(*)').eq('id', context.candidateId).single();

        const flowClientId = flow.client_id || context.vacancyData?.client_id;

        if (!cData && flowClientId) {
            // Might be a candidates.id passed in, look up the profile dynamically
            const { data: altData } = await supabase.from('candidates').select('*, candidate:candidates(*)').eq('candidate_id', context.candidateId).eq('client_id', flowClientId).single();
            cData = altData;
        }

        if (cData) {
            context.candidateData = cData.candidate || cData.candidates || cData;
            context.profileId = cData.id; // Important for drafts
        }
    }

    // Force resolve profileId if not set, to satisfy candidate_communications FK
    if (!context.profileId && context.candidateId) {
        const { data: isProfile } = await supabase.from('candidates').select('id').eq('id', context.candidateId).maybeSingle();
        if (isProfile) {
            context.profileId = isProfile.id;
        } else {
            const flowClientId = flow.client_id || context.vacancyData?.client_id;
            let query = supabase.from('candidates').select('id').eq('candidate_id', context.candidateId);
            if (flowClientId) query = query.eq('client_id', flowClientId);
            const { data: profileRef } = await query.limit(1).maybeSingle();
            if (profileRef) context.profileId = profileRef.id;
        }
    }

    if (!context.userData && context.user_id) {
        const { data: uData } = await supabase.from('profiles').select('*').eq('id', context.user_id).single();
        if (uData) context.userData = uData;
    }

    switch (stepType) {
        case 'email': {
            let toEmail = step.config?.to || '{{candidate.email}}';
            toEmail = parseVariables(toEmail, context);

            let htmlContent = '';
            let subjectStr = '';

            // Fetch template
            if (step.config?.templateId) {
                const { data: tData } = await supabase.from('automation_templates').select('*').eq('id', step.config.templateId).single();
                if (tData) {
                    htmlContent = parseVariables(tData.content || '', context);
                    subjectStr = parseVariables(tData.subject || 'Werftools Update', context);
                }
            } else {
                subjectStr = parseVariables(step.config?.subject || 'Werftools Update', context);
                htmlContent = `<p>${parseVariables(step.config?.customMessage || '', context)}</p>`;
            }

            if (!toEmail || !toEmail.includes('@')) {
                const msg = "Ongeldig emailadres: " + toEmail;
                console.log("[Automation] " + msg);
                executionTrace.push({ stepId: step.id, type: stepType, label: step.label, status: 'failed', details: msg, timestamp: Date.now() });
                break;
            }

            // Call Backend SendGrid Endpoint OR Database Draft
            try {
                if (step.config?.sendMode === 'manual') {
                    // Stage as Draft
                    const { error: insertErr } = await supabase.from('candidate_communications').insert({
                        profile_id: context.profileId,
                        user_id: context.user_id || null,
                        type: 'email_draft',
                        content: `⏳ Klaargezet door Automation (${flow.name}): \n\nAan: ${toEmail}\nOnderwerp: ${subjectStr}\n\n${htmlContent.replace(/<[^>]+>/g, '')}`
                    });
                    if (insertErr) throw insertErr;

                    const msg = `Concept klaargezet voor ${toEmail}`;
                    console.log(`[Automation] ${msg}`);
                    executionTrace.push({ stepId: step.id, type: stepType, label: step.label, status: 'success', details: msg, timestamp: Date.now() });
                } else {
                    // Send Automatically via Microsoft Graph API
                    const baseApiUrl = (import.meta as any).env?.VITE_API_URL || '';
                    const apiUrl = `${baseApiUrl}/api/automations/send-email`;

                    // We need the provider_token (Microsoft Access Token) to send via Graph API
                    const { data: sessionData } = await supabase.auth.getSession();
                    const providerToken = sessionData?.session?.provider_token;

                    if (!providerToken) {
                        const errMsg = "Geen Microsoft Access Token gevonden. Log opnieuw in met Microsoft om e-mails te verzenden.";
                        console.error("[Automation]", errMsg);
                        executionTrace.push({ stepId: step.id, type: stepType, label: step.label, status: 'failed', details: errMsg, timestamp: Date.now() });
                        throw new Error(errMsg);
                    }

                    const res = await fetch(apiUrl, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ 
                            to: toEmail, 
                            subject: subjectStr, 
                            html: htmlContent,
                            providerToken: providerToken 
                        })
                    });

                    if (res.ok) {
                        const { error: insertErr } = await supabase.from('candidate_communications').insert({
                            profile_id: context.profileId,
                            user_id: context.user_id || null,
                            type: 'email',
                            content: `✅ Email Automation Verzonden via Microsoft 365: \n\nAan: ${toEmail}\nOnderwerp: ${subjectStr}\n\n${htmlContent.replace(/<[^>]+>/g, '')}`
                        });
                        if (insertErr) console.warn("Log insert failed", insertErr);

                        const msg = `✅ Automatisch verzonden naar ${toEmail} via Microsoft 365`;
                        console.log(`[Automation] ${msg}`);
                        executionTrace.push({ stepId: step.id, type: stepType, label: step.label, status: 'success', details: msg, timestamp: Date.now() });
                    } else {
                        throw new Error(await res.text());
                    }
                }
            } catch (err: any) {
                const msg = err.message || "Email endpoint failed";
                console.error("[Automation] Fetch failed for email", err);
                executionTrace.push({ stepId: step.id, type: stepType, label: step.label, status: 'failed', details: msg, timestamp: Date.now() });
                throw err;
            }
            break;
        }

        case 'whatsapp': {
            const rawMsg = step.config?.customMessage || 'Hallo {{candidate.first_name}}, heb je even tijd?';
            const parsedMsg = parseVariables(rawMsg, context);
            const toPhone = parseVariables(step.config?.to || '{{candidate.phone}}', context);

            if (!toPhone) {
                const msg = "Geen telefoonnummer gevonden voor " + context.candidateData?.name;
                console.log("[Automation] " + msg);
                executionTrace.push({ stepId: step.id, type: stepType, label: step.label, status: 'failed', details: msg, timestamp: Date.now() });
                break;
            }

            try {
                if (step.config?.sendMode === 'manual') {
                    // Stage as Draft
                    const { error: insertErr } = await supabase.from('candidate_communications').insert({
                        profile_id: context.profileId,
                        user_id: context.user_id || null,
                        type: 'whatsapp_draft',
                        content: `⏳ Klaargezet door Automation (${flow.name}): \n\nAan: ${toPhone}\n\n${parsedMsg}`
                    });
                    if (insertErr) throw insertErr;

                    const msg = `Concept klaargezet voor ${toPhone}`;
                    console.log(`[Automation] ${msg}`);
                    executionTrace.push({ stepId: step.id, type: stepType, label: step.label, status: 'success', details: msg, timestamp: Date.now() });
                } else {
                    // Send Automatically
                    const baseApiUrl = (import.meta as any).env?.VITE_API_URL || '';
                    const apiUrl = `${baseApiUrl}/api/automations/send-whatsapp`;

                    const recruiterPhone = context.userData?.phone || '';

                    const res = await fetch(apiUrl, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ 
                            to: toPhone, 
                            body: parsedMsg,
                            fromPhone: recruiterPhone 
                        })
                    });

                    if (res.ok) {
                        const { error: insertErr } = await supabase.from('candidate_communications').insert({
                            profile_id: context.profileId || context.candidateId,
                            user_id: context.user_id || null,
                            type: 'whatsapp',
                            content: `✅ WhatsApp Automation Verzonden:\n\nAan: ${toPhone}\n\n${parsedMsg}`
                        });
                        if (insertErr) console.warn("Log insert failed", insertErr);

                        const msg = `✅ Automatisch verzonden naar ${toPhone} via Bird (WhatsApp API)`;
                        console.log(`[Automation] ${msg}`);
                        executionTrace.push({ stepId: step.id, type: stepType, label: step.label, status: 'success', details: msg, timestamp: Date.now() });
                    } else {
                        throw new Error(await res.text());
                    }
                }
            } catch (err) {
                console.error("[Automation] WhatsApp endpoint failed:", err);
                throw err;
            }
            break;
        }

        case 'create_task': {
            if (context.user_id) {
                const { error: insertErr } = await supabase.from('todos').insert({
                    user_id: context.user_id,
                    vacancy_id: context.vacancyId,
                    candidate_id: context.candidateId,
                    title: step.config?.taskName || 'Automation Task',
                    description: 'Created by automation flow',
                    due_date: new Date(Date.now() + 86400000).toISOString(),
                    priority: 'medium',
                    status: 'pending'
                });
                if (insertErr) {
                    executionTrace.push({ stepId: step.id, type: stepType, label: step.label, status: 'failed', details: insertErr.message, timestamp: Date.now() });
                } else {
                    const msg = "Taak aangemaakt";
                    console.log(`[Automation] ${msg}`);
                    executionTrace.push({ stepId: step.id, type: stepType, label: step.label, status: 'success', details: msg, timestamp: Date.now() });
                }
            } else {
                executionTrace.push({ stepId: step.id, type: stepType, label: step.label, status: 'skipped', details: 'Geen user_id gevonden', timestamp: Date.now() });
            }
            break;
        }

        case 'internal_note': {
            const { error: insertErr } = await supabase.from('candidate_communications').insert({
                profile_id: context.profileId,
                user_id: context.user_id,
                type: 'note',
                content: `[Automation] ${step.config?.note || 'Auto note'}`
            });
            if (insertErr) {
                executionTrace.push({ stepId: step.id, type: stepType, label: step.label, status: 'failed', details: insertErr.message, timestamp: Date.now() });
            } else {
                const msg = "Interne notitie toegevoegd";
                console.log(`[Automation] ${msg}`);
                executionTrace.push({ stepId: step.id, type: stepType, label: step.label, status: 'success', details: msg, timestamp: Date.now() });
            }
            break;
        }

        case 'stage_change': {
            const msg = "Fase wijziging overgeslagen (anti-loop)";
            console.log(`[Automation] ${msg}`);
            executionTrace.push({ stepId: step.id, type: stepType, label: step.label, status: 'skipped', details: msg, timestamp: Date.now() });
            break;
        }

        case 'sms': {
            const rawMsg = step.config?.customMessage || 'Hallo {{candidate.first_name}}';
            const parsedMsg = parseVariables(rawMsg, context);
            const toPhone = parseVariables(step.config?.to || '{{candidate.phone}}', context);

            if (!toPhone) {
                const msg = "Geen telefoonnummer gevonden voor " + context.candidateData?.name;
                console.log("[Automation] " + msg);
                executionTrace.push({ stepId: step.id, type: stepType, label: step.label, status: 'failed', details: msg, timestamp: Date.now() });
                break;
            }

            try {
                if (step.config?.sendMode === 'manual') {
                    // Stage as Draft
                    const { error: insertErr } = await supabase.from('candidate_communications').insert({
                        profile_id: context.profileId,
                        user_id: context.user_id || null,
                        type: 'sms_draft',
                        content: `⏳ Klaargezet door Automation (${flow.name}): \n\nAan: ${toPhone}\n\n${parsedMsg}`
                    });
                    if (insertErr) throw insertErr;

                    const msg = `SMS Concept klaargezet voor ${toPhone}`;
                    console.log(`[Automation] ${msg}`);
                    executionTrace.push({ stepId: step.id, type: stepType, label: step.label, status: 'success', details: msg, timestamp: Date.now() });
                } else {
                    // Send Automatically
                    const baseApiUrl = (import.meta as any).env?.VITE_API_URL || '';
                    const apiUrl = `${baseApiUrl}/api/automations/send-sms`;

                    const res = await fetch(apiUrl, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ to: toPhone, body: parsedMsg })
                    });

                    if (res.ok) {
                        const { error: insertErr } = await supabase.from('candidate_communications').insert({
                            profile_id: context.profileId,
                            user_id: context.user_id || null,
                            type: 'sms',
                            content: `✅ SMS Automation Verzonden:\n\nAan: ${toPhone}\n\n${parsedMsg}`
                        });
                        if (insertErr) console.warn("Log insert failed", insertErr);

                        const msg = `✅ SMS Automatisch verzonden naar ${toPhone} via Bird API`;
                        console.log(`[Automation] ${msg}`);
                        executionTrace.push({ stepId: step.id, type: stepType, label: step.label, status: 'success', details: msg, timestamp: Date.now() });
                    } else {
                        throw new Error(await res.text());
                    }
                }
            } catch (err) {
                console.error("[Automation] SMS endpoint failed:", err);
                throw err;
            }
            break;
        }

        case 'n8n_webhook': {
            const webhookUrl = step.config?.webhookUrl || step.config?.url;
            console.log(`[Automation] Routing context payload to n8n Webhook: ${webhookUrl}`);
            
            if (!webhookUrl) {
                const msg = "Ongeldige (lege) n8n Webhook URL";
                executionTrace.push({ stepId: step.id, type: stepType, label: step.label || 'n8n Webhook', status: 'failed', details: msg, timestamp: Date.now() });
                break;
            }

            try {
                const n8nPayload = {
                    event: flow.trigger_type || 'custom',
                    timestamp: new Date().toISOString(),
                    context: {
                        candidate: context.candidateData,
                        vacancy: context.vacancyData,
                        user: context.userData,
                        custom_event: context.customEventName,
                        stage: context.stageName
                    }
                };

                const res = await postWebhookProxyOrDirect(webhookUrl, n8nPayload);

                if (res.ok) {
                    const msg = `✅ Payload succesvol verzonden naar n8n`;
                    console.log(`[Automation] ${msg}`);
                    executionTrace.push({ stepId: step.id, type: stepType, label: step.label || 'n8n Webhook', status: 'success', details: msg, timestamp: Date.now() });
                } else {
                    throw new Error(`n8n HTTP Error: ${res.status}`);
                }
            } catch (err: any) {
                console.error("[Automation] n8n webhook POST failed:", err);
                executionTrace.push({ stepId: step.id, type: stepType, label: step.label || 'n8n Webhook', status: 'failed', details: err.message || 'Verbingsfout met n8n', timestamp: Date.now() });
                throw err;
            }
            break;
        }

        case 'condition': {
            const field = step.config?.conditionField;
            const operator = step.config?.conditionOperator;
            const expectedValue = step.config?.conditionValue;

            if (!field || !operator) {
                const msg = "Condition node missing config, skipping...";
                console.log(`[Automation] ${msg}`);
                executionTrace.push({ stepId: step.id, type: stepType, label: step.label, status: 'skipped', details: msg, timestamp: Date.now() });
                break;
            }

            const isTrue = checkCondition({ field, operator, value: expectedValue }, context);

            if (!isTrue) {
                console.log(`[Automation] Condition failed: ${field} ${operator} ${expectedValue}`);
                throw new Error(`Condition failed: ${field} ${operator} ${expectedValue}. Logic branch stopped.`);
            } else {
                const msg = `Condition passed: ${field} ${operator} ${expectedValue}`;
                console.log(`[Automation] ${msg}`);
                executionTrace.push({ stepId: step.id, type: stepType, label: step.label, status: 'success', details: msg, timestamp: Date.now() });
            }
            break;
        }

        case 'branch': {
            console.log(`[Automation] Evaluating branch step ${step.id}...`);
            let executed = false;
            for (const branch of step.config.branches || []) {
                if (evaluateBranch(branch, context)) {
                    const msg = `Branch '${branch.name}' conditions met. Executing nested steps.`;
                    console.log(`[Automation] ${msg}`);
                    executionTrace.push({ stepId: step.id, type: stepType, label: step.label || 'Splitsing', status: 'success', details: msg, timestamp: Date.now() });

                    // Recursive call to execute the branch's nested steps - PASS THE TRACE DOWN!
                    await executeStepsSequence(branch.steps || [], context, flow, executionTrace);
                    executed = true;
                    break; // Run only the first branch that passes
                }
            }
            if (!executed) {
                const msg = `No branches matched criteria, moving past branch.`;
                console.log(`[Automation] ${msg}`);
                executionTrace.push({ stepId: step.id, type: stepType, label: step.label || 'Splitsing', status: 'skipped', details: msg, timestamp: Date.now() });
            }
            break;
        }

        default: {
            const msg = `Unknown step type: ${stepType}`;
            console.log(`[Automation] ${msg}`);
            executionTrace.push({ stepId: step.id, type: stepType, label: step.label, status: 'skipped', details: msg, timestamp: Date.now() });
        }
    }
};
